import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Loader } from '@/components/Loader';
import { useRouter } from '@/hooks/useRouter';
import { useUser } from '@/hooks/useUser';
import { paths } from '@/lib/paths';
import type { WithChildren } from '@/lib/types';

export const LoginRequired = ({ children }: WithChildren) => {
  const {
    fetchUser,
    snapshot: { isAuthenticated },
  } = useUser();
  const [isFetching, setIsFetching] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const { asPath, replace } = useRouter();
  const hasRedirected = useRef(false);

  const redirectToLoginPage = () => {
    if (!hasRedirected.current) {
      // setTimeout ensures redirection after React rendering to avoid Next.js routing issues
      setTimeout(() => {
        replace(paths.myAccount.login.url({ query: { next: asPath } }));
        hasRedirected.current = true;
      }, 100);
    }
  };

  useEffect(() => {
    const initializeUser = async () => {
      if (
        (isAuthenticated === undefined || isAuthenticated === null) &&
        !isFetching
      ) {
        setIsFetching(true);
        try {
          // Await fetchUser to handle asynchronous operations and ensure accurate authentication status
          await fetchUser();
        } finally {
          setIsFetching(false);
          // Mark initialization as complete
          setIsInitialized(true);
        }
      } else {
        // Mark initialization as complete if already authenticated
        setIsInitialized(true);
      }
    };

    initializeUser();
  }, [isAuthenticated, fetchUser]);

  // useLayoutEffect ensures redirection occurs after the component has been rendered and the DOM updates have been flushed
  useLayoutEffect(() => {
    if (isInitialized && isAuthenticated === false && !hasRedirected.current) {
      redirectToLoginPage();
    }
  }, [isInitialized, isAuthenticated]);

  if (
    isFetching ||
    !isInitialized ||
    isAuthenticated === undefined ||
    isAuthenticated === null
  ) {
    return (
      <Loader
        data-test-id='LoginRequiredLoader'
        data-testid='LoginRequiredLoader'
      />
    );
  }

  if (isAuthenticated) {
    return <>{children}</>;
  }

  // Return null to prevent rendering the content when not authenticated
  return null;
};
